import React from 'react';
import PropTypes from 'prop-types';

import RatingCell from './RatingCell';
import RecentResultsCell from './RecentResultsCell';
import EventActivityCell from './EventActivityCell';
import PlayerIdentity from './PlayerIdentity';
import RowKebab from './RowKebab';
import NoteIndicator from './NoteIndicator';
import { SEE_MORE_LABEL, SEE_LESS_LABEL, COLUMN_LABELS, SINGLES_ICON, DOUBLES_ICON } from './constants';
import { MatchFormatTypes } from '../../../../utils/postScoreConstants';
import styles from './playersList.scss';

// Mobile expandable card (82-4886). Story 17 shows identity + the two rating cells always; the See
// More / See Less toggle reveals the details region that Story 18 (TMS-2109) fills with the Recent
// Results + Event Activity sections (same content rules as the desktop cells). Expansion is controlled
// by the parent so the header's Expand All / Collapse All can drive every card at once.
const PlayerCardMobile = ({ player, isExpanded, onToggleExpand, onNote, onRemove, 'data-testid': dataTestId }) => {
    const { recentActivity, recentResults, eventActivity, note } = player;

    return (
        <div className={styles.card} data-testid={dataTestId || undefined}>
            <div className={styles.cardHeader}>
                <span className={styles.activityCol}>
                    {recentActivity && (
                        <span className={styles.activityDot} data-testid={dataTestId && `${dataTestId}.activity-dot`} />
                    )}
                </span>
                <PlayerIdentity player={player} data-testid={dataTestId} />
                {note && (
                    <NoteIndicator onClick={onNote} data-testid={dataTestId && `${dataTestId}.note-icon`} />
                )}
                <RowKebab onNote={onNote} onRemove={onRemove} data-testid={dataTestId && `${dataTestId}.menu`} />
            </div>
            <div className={styles.cardRatings}>
                <div className={styles.cardRatingItem}>
                    <img className={styles.cardRatingIcon} src={SINGLES_ICON} alt="" data-testid={dataTestId && `${dataTestId}.singles-icon`} />
                    <RatingCell player={player} ratingType={MatchFormatTypes.SINGLES} data-testid={dataTestId && `${dataTestId}.singles`} />
                </div>
                <div className={styles.cardRatingItem}>
                    <img className={styles.cardRatingIcon} src={DOUBLES_ICON} alt="" data-testid={dataTestId && `${dataTestId}.doubles-icon`} />
                    <RatingCell player={player} ratingType={MatchFormatTypes.DOUBLES} data-testid={dataTestId && `${dataTestId}.doubles`} />
                </div>
                <button
                    type="button"
                    className={styles.expandToggle}
                    onClick={onToggleExpand}
                    data-testid={dataTestId && `${dataTestId}.expand`}
                >
                    {isExpanded ? SEE_LESS_LABEL : SEE_MORE_LABEL}
                    <i className={`fas ${isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
                </button>
            </div>
            {isExpanded && (
                <div className={styles.cardDetails} data-testid={dataTestId && `${dataTestId}.details`}>
                    <div className={styles.cardSection}>
                        <div className={styles.cardSectionLabel} data-testid={dataTestId && `${dataTestId}.details.recent-results-label`}>
                            {COLUMN_LABELS.RECENT_RESULTS}
                        </div>
                        <RecentResultsCell results={recentResults} data-testid={dataTestId && `${dataTestId}.recent-results`} />
                    </div>
                    <div className={styles.cardSection}>
                        <div className={styles.cardSectionLabel} data-testid={dataTestId && `${dataTestId}.details.event-activity-label`}>
                            {COLUMN_LABELS.EVENT_ACTIVITY}
                        </div>
                        <EventActivityCell events={eventActivity} data-testid={dataTestId && `${dataTestId}.event-activity`} />
                    </div>
                </div>
            )}
        </div>
    );
};

PlayerCardMobile.propTypes = {
    player: PropTypes.instanceOf(Object).isRequired,
    isExpanded: PropTypes.bool,
    onToggleExpand: PropTypes.func,
    onNote: PropTypes.func,
    onRemove: PropTypes.func,
    'data-testid': PropTypes.string,
};

PlayerCardMobile.defaultProps = {
    isExpanded: false,
    onToggleExpand: () => {},
    onNote: () => {},
    onRemove: () => {},
    'data-testid': null,
};

export default PlayerCardMobile;
